/* SundayRecap — soft weekly summary. Sofia's Sunday ritual: what happened this week? */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Sparkles, Clock, Flame, Feather, MessageCircle, Rocket, Dumbbell, Repeat, Share2, Trophy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAY_SHORT = ['sö', 'må', 'ti', 'on', 'to', 'fr', 'lö'];

function StatCard({ icon: Icon, label, value, subtitle, color }) {
  return (
    <div className={`rounded-2xl p-4 border ${color}`} data-testid={`sunday-stat-${label}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase font-medium tracking-wider opacity-70">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-3xl font-bold mt-1">{value}</div>
      {subtitle && <div className="text-[11px] opacity-70 mt-0.5">{subtitle}</div>}
    </div>
  );
}

export default function SundayRecap() {
  const [data, setData] = useState(null);
  const [monthlyHabits, setMonthlyHabits] = useState({ days: [], habits: [] });
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: m }] = await Promise.all([
        axios.get(`${API}/sunday/recap`, { withCredentials: true }),
        axios.get(`${API}/daily-flow/habits/heatmap?days=28`, { withCredentials: true }).catch(() => ({ data: { days: [], habits: [] } })),
      ]);
      setData(r);
      setMonthlyHabits({ days: m.days || [], habits: m.habits || [] });
    } catch {
      toast.error('Kunde inte ladda veckorekapen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onShare = async () => {
    if (!data || sharing) return;
    setSharing(true);
    try {
      const { shareOrDownloadSundayWrap } = await import('./sundayWrapCard');
      const res = await shareOrDownloadSundayWrap(data);
      if (res.shared) toast.success('Delad 💜');
      else if (res.downloaded) toast.success('Bilden sparad — redo att dela 💜');
    } catch (err) {
      toast.error('Kunde inte skapa bilden');
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        Samlar veckan…
      </div>
    );
  }
  if (!data) return null;

  const perDay = data.daily_flow?.per_day || {};
  const maxDay = Math.max(1, ...Object.values(perDay));

  return (
    <div className="max-w-3xl mx-auto space-y-4 py-2" data-testid="sunday-recap-page">
      {/* Hero */}
      <div className="rounded-3xl p-6 bg-gradient-to-br from-violet-50 via-rose-50 to-amber-50 border border-violet-100 relative overflow-hidden">
        <div className="absolute top-2 right-3 opacity-15 text-8xl leading-none pointer-events-none">🕊</div>
        <div className="text-[11px] uppercase font-semibold tracking-wider text-violet-700/70">Söndags-rituell</div>
        <h1 className="text-3xl font-bold text-violet-900 mt-1" style={{ fontFamily: 'ui-serif, Georgia, serif' }}>
          Veckan som var
        </h1>
        <p className="text-xs text-violet-700/60 mt-1">
          {data.range?.start} → {data.range?.end}
          {data.is_sunday ? ' · Perfekt söndag för en mjuk återblick 💜' : ''}
        </p>
        <div className="mt-4 flex items-baseline gap-3">
          <div className="text-5xl font-bold text-violet-900">{data.score}</div>
          <div className="text-xs text-slate-500 pb-1">veckopoäng</div>
        </div>
        <p className="text-sm italic text-violet-800 mt-2">{data.vibe}</p>
        <button
          type="button"
          onClick={onShare}
          disabled={sharing}
          data-testid="sunday-share-btn"
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500 to-rose-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
          {sharing ? 'Skapar bild…' : 'Spara som story-bild'}
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Sparkles}
          label="Klara punkter"
          value={data.daily_flow?.completed || 0}
          subtitle={data.daily_flow?.lingering ? `${data.daily_flow.lingering} dröjer` : 'i idag-listan'}
          color="bg-emerald-50 border-emerald-100 text-emerald-900"
        />
        <StatCard
          icon={Clock}
          label="Fokus"
          value={data.focus?.sessions || 0}
          subtitle={`${data.focus?.minutes || 0} min totalt`}
          color="bg-sky-50 border-sky-100 text-sky-900"
        />
        <StatCard
          icon={Rocket}
          label="Tidsresor"
          value={data.timefeed?.count || 0}
          subtitle={data.timefeed?.latest?.role_label || 'inga sparade'}
          color="bg-amber-50 border-amber-100 text-amber-900"
        />
        <StatCard
          icon={Dumbbell}
          label="Träning"
          value={data.workouts?.count || 0}
          subtitle={
            Object.keys(data.workouts?.by_goal || {}).length > 0
              ? Object.keys(data.workouts.by_goal).slice(0, 2).join(', ')
              : 'inga pass'
          }
          color="bg-rose-50 border-rose-100 text-rose-900"
        />
      </div>

      {/* Daily flow per-day bars */}
      {Object.keys(perDay).length > 0 && (
        <div className="rounded-2xl p-4 bg-white border border-slate-100">
          <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-emerald-500" /> Punkter per dag
          </div>
          <div className="flex items-end gap-1.5 h-24">
            {(data.range?.days || []).map((d) => {
              const n = perDay[d] || 0;
              const h = Math.max(4, Math.round((n / maxDay) * 90));
              const dt = new Date(d + 'T00:00:00');
              const wd = WEEKDAY_SHORT[dt.getDay()];
              return (
                <div key={d} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="text-[10px] text-slate-400 group-hover:text-slate-700">{n || ''}</div>
                  <div
                    className={`w-full rounded-t-md ${n > 0 ? 'bg-gradient-to-t from-emerald-300 to-emerald-500' : 'bg-slate-100'}`}
                    style={{ height: `${h}px` }}
                  />
                  <div className="text-[10px] text-slate-500">{wd}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top habits */}
      {data.habits && data.habits.length > 0 && (
        <div className="rounded-2xl p-4 bg-white border border-slate-100">
          <div className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5">
            <Repeat className="w-3.5 h-3.5 text-emerald-500" /> Bäst hållna vanor
          </div>
          <div className="space-y-2">
            {data.habits.map((h) => (
              <div key={h.text} className="flex items-center gap-3">
                <div className="text-sm text-slate-800 flex-1 truncate">{h.text}</div>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-300 to-emerald-500"
                    style={{ width: `${Math.round((h.days_done / h.total) * 100)}%` }}
                  />
                </div>
                <div className="text-xs text-slate-500 whitespace-nowrap">
                  <Flame className="w-3 h-3 inline text-amber-500" /> {h.days_done}/{h.total}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly habit heatmap — 4-week long-term view */}
      {monthlyHabits.habits.length > 0 && (
        <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-50/50 to-white border border-emerald-100" data-testid="monthly-habit-heatmap">
          <div className="text-sm font-semibold text-emerald-900 mb-1 flex items-center gap-1.5">
            <Repeat className="w-3.5 h-3.5" /> 4-veckors mönster
          </div>
          <div className="text-[11px] text-emerald-700/70 mb-3">
            Se långtidsmönster — varje ruta är en dag ({monthlyHabits.days[0]} → {monthlyHabits.days.slice(-1)[0]})
          </div>
          <div className="space-y-2">
            {monthlyHabits.habits.map((h) => (
              <div key={h.text} className="flex items-center gap-2" data-testid={`monthly-heatmap-row-${h.id}`}>
                <div className="w-[110px] text-xs text-slate-700 truncate pr-1 flex-shrink-0" title={h.text}>{h.text}</div>
                <div className="flex-1 grid gap-[3px] min-w-0" style={{ gridTemplateColumns: `repeat(${monthlyHabits.days.length}, minmax(0, 1fr))` }}>
                  {h.cells.map((c, i) => {
                    const isToday = monthlyHabits.days[i] === (data.range?.end || '');
                    return (
                      <div
                        key={i}
                        className={`aspect-square rounded-sm ${
                          c ? 'bg-emerald-400' : 'bg-slate-100'
                        } ${isToday ? 'ring-1 ring-emerald-500' : ''}`}
                        title={`${monthlyHabits.days[i]}${c ? ' · klarat ✨' : ''}`}
                      />
                    );
                  })}
                </div>
                <div className="text-[10px] text-slate-500 whitespace-nowrap w-[70px] text-right">
                  {h.streak > 0 && <span className="text-emerald-700 font-bold">🔥 {h.streak}d</span>}
                  <div className="text-slate-400">{h.completion_pct}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skincare summary */}
      {data.skincare && (data.skincare.completed > 0 || data.skincare.mood_logs > 0 || (data.skincare.insights || []).length > 0 || (data.skincare.low_stock || []).length > 0) && (
        <div className="rounded-2xl p-4 bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200" data-testid="sunday-skincare">
          <div className="text-sm font-semibold text-rose-900 flex items-center gap-1.5 mb-2">
            🧴 Hudrutin denna vecka
          </div>
          <p className="text-sm text-rose-800">
            {data.skincare.completed > 0 && <><b>{data.skincare.completed}</b> rutiner klarade ✨</>}
            {data.skincare.completed > 0 && data.skincare.mood_logs > 0 && ' · '}
            {data.skincare.mood_logs > 0 && <><b>{data.skincare.mood_logs}</b> humör-loggar</>}
          </p>
          {(data.skincare.top_moods || []).length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase font-bold text-rose-700/70 tracking-wider mb-1">Hudens humör den här veckan</div>
              <div className="flex flex-wrap gap-1.5">
                {data.skincare.top_moods.map((m) => (
                  <span key={m.mood} className="text-xs bg-white border border-rose-200 rounded-full px-2 py-0.5 text-rose-800">
                    {m.mood} <span className="text-rose-400">×{m.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {(data.skincare.insights || []).length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="sunday-skincare-insights">
              <div className="text-[10px] uppercase font-bold text-rose-700/70 tracking-wider">Mönster jag ser</div>
              {data.skincare.insights.map((i, idx) => (
                <div
                  key={idx}
                  className={`text-xs px-2.5 py-1.5 rounded-lg border ${
                    i.kind === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  }`}
                >
                  {i.kind === 'warn' ? '⚠️ ' : '✨ '}{i.text}
                </div>
              ))}
            </div>
          )}
          {(data.skincare.low_stock || []).length > 0 && (
            <div className="mt-3 text-xs rounded-lg bg-amber-50 border border-amber-200 p-2 text-amber-900" data-testid="sunday-skincare-lowstock">
              <b>🛍 Nästan slut:</b> {data.skincare.low_stock.map((l) => l.name).join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Family play scoreboard */}
      {data.family_play && data.family_play.count > 0 && (
        <div className="rounded-2xl p-4 bg-gradient-to-br from-orange-50 to-amber-50 border border-amber-200" data-testid="sunday-family-play">
          <div className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
            <Trophy className="w-3.5 h-3.5 text-amber-600" /> Familjetävling denna vecka
          </div>
          <p className="text-sm text-amber-800 mb-3">
            Vi lekte <span className="font-bold">{data.family_play.count}</span> {data.family_play.count === 1 ? 'lek' : 'lekar'} tillsammans denna vecka 🎉
          </p>
          {(data.family_play.kid_scores || []).length > 0 && (
            <div className="space-y-1.5 mb-3">
              {data.family_play.kid_scores.map((k, i) => (
                <div key={k.name} className="flex items-center gap-2">
                  <div className="w-6 text-sm">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '👦'}</div>
                  <div className="text-sm text-slate-800 flex-1">{k.name}</div>
                  <div className="text-sm font-bold text-amber-800">{k.points}p</div>
                </div>
              ))}
            </div>
          )}
          {(data.family_play.recent_titles || []).length > 0 && (
            <div className="text-[11px] text-amber-700/80 italic">
              Bl.a: {data.family_play.recent_titles.join(' · ')}
            </div>
          )}
        </div>
      )}

      {/* Iris highlights */}
      {data.iris_highlights && data.iris_highlights.length > 0 && (
        <div className="rounded-2xl p-4 bg-gradient-to-br from-fuchsia-50 to-rose-50 border border-rose-100">
          <div className="text-sm font-semibold text-rose-900 mb-2 flex items-center gap-1.5">
            <MessageCircle className="w-3.5 h-3.5" /> Iris sa till dig
          </div>
          <div className="space-y-2">
            {data.iris_highlights.map((m, i) => (
              <div key={i} className="text-sm italic text-rose-800 border-l-2 border-rose-300 pl-3 bg-white/50 rounded-r-md py-1">
                “{m.text}”
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timefeed highlight */}
      {data.timefeed?.latest && (
        <div className="rounded-2xl p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
          <div className="text-sm font-semibold text-amber-900 mb-1 flex items-center gap-1.5">
            <Rocket className="w-3.5 h-3.5" /> Senaste tidsresan
          </div>
          <div className="text-sm text-amber-800">
            {data.timefeed.latest.role_label && (
              <div className="text-[11px] uppercase font-bold text-amber-700/70 tracking-wider mb-0.5">
                {data.timefeed.latest.role_label}
              </div>
            )}
            <div className="italic">“{data.timefeed.latest.hook}”</div>
          </div>
        </div>
      )}

      {/* Closing */}
      <div className="rounded-2xl p-5 bg-gradient-to-br from-slate-50 to-white border border-slate-100 text-center">
        <Feather className="w-5 h-5 text-violet-400 mx-auto mb-2" />
        <p className="text-sm text-slate-700 italic">
          En vecka behöver inte vara perfekt för att vara värdefull.<br />
          Du gjorde det du kunde. Nu vilar vi. 💜
        </p>
      </div>
    </div>
  );
}
